import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiMenu, FiX, FiPhone, FiMail, FiFacebook, FiInstagram, FiYoutube, FiGrid } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${props => props.theme.zIndex.sticky};
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  width: 100%;
  padding: 0.5rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  /* Ensure sticky behavior works on all devices */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  
  /* 150% zoom and small laptops */
  @media (min-width: 1024px) and (max-width: 1366px) {
    padding: 0.6rem 0;
  }

  /* 175% zoom and medium laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    padding: 0.55rem 0;
  }

  /* 200% zoom and large screens */
  @media (min-width: 1600px) and (max-width: 1920px) {
    padding: 0.5rem 0;
  }
  
  /* iPad specific adjustments */
  @media (min-width: 768px) and (max-width: 1024px) {
    padding: 0.75rem 0;
  }
`;

const HeaderBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(1, 103, 95, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: -1;
`;

const TopBar = styled.div`
  position: relative;
  height: 50px;
  
  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    height: 45px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    height: 40px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TopBarContent = styled.div`
  position: absolute;
  right: 2rem;
  top: 0;
  background: ${props => props.theme.colors.button};
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem 0.75rem 1rem;
  border-radius: 0 0 25px 25px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 10;
  min-height: 40px;

  /* 150% zoom and small laptops */
  @media (min-width: 1024px) and (max-width: 1366px) {
    right: 1.75rem;
    padding: 0.45rem 0.9rem 0.7rem 0.9rem;
    border-radius: 0 0 22px 22px;
    font-size: 0.78rem;
    gap: 0.9rem;
    min-height: 38px;
  }

  /* 175% zoom and medium laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    right: 1.5rem;
    padding: 0.4rem 0.85rem 0.65rem 0.85rem;
    border-radius: 0 0 20px 20px;
    font-size: 0.76rem;
    gap: 0.85rem;
    min-height: 36px;
  }

  /* 200% zoom and large screens */
  @media (min-width: 1600px) and (max-width: 1920px) {
    right: 1.25rem;
    padding: 0.35rem 0.8rem 0.6rem 0.8rem;
    border-radius: 0 0 18px 18px;
    font-size: 0.74rem;
    gap: 0.8rem;
    min-height: 34px;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    right: 1.5rem;
    padding: 0.4rem 0.8rem 0.6rem 0.8rem;
    border-radius: 0 0 20px 20px;
    font-size: 0.75rem;
    gap: 0.8rem;
    min-height: 35px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    right: 1rem;
    padding: 0.3rem 0.6rem 0.5rem 0.6rem;
    border-radius: 0 0 18px 18px;
    font-size: 0.7rem;
    gap: 0.6rem;
    min-height: 30px;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SocialIcon = styled.a`
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
`;

const GetStartedBtn = styled(Link)`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 800;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }

  &.desktop-only {
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const DashboardBtn = styled(Link)`
  background: ${props => props.theme.colors.secondary};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.secondaryDark};
    transform: translateY(-1px);
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }

  &.desktop-only {
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const MainHeader = styled.div`
  background: white;
  padding: 1rem 0;
  margin: 0 2rem;
  border-radius: 50px;
  box-shadow: ${props => props.theme.shadows.lg};
  margin-top: -10px;
  position: relative;
  z-index: 2;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    margin: -10px 1.5rem 0 1.5rem;
    border-radius: 40px;
    padding: 0.75rem 0;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    margin: -10px 1rem 0 1rem;
    border-radius: 35px;
    padding: 0.5rem 0;
  }

  @media (max-width: 768px) {
    margin: -10px 1rem 0 1rem;
    border-radius: 30px;
  }
`;

const MainHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 0 1.5rem;
    max-width: 100%;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 0 1rem;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const LogoIcon = styled.img`
  width: 120px;
  height: 60px;
  border-radius: 8px;
  object-fit: contain;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    width: 100px;
    height: 50px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    width: 90px;
    height: 45px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    gap: 1.5rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 700;
  transition: color 0.3s ease;
  position: relative;
  outline: none;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &.active {
    color: ${props => props.theme.colors.primary};
    font-weight: 800;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    gap: 0.75rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    gap: 0.5rem;
  }
`;

const SearchButton = styled.button`
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 100;
  position: relative;

  @media (max-width: 768px) {
    display: block !important;
  }

  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  z-index: 9999;
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  padding: 2rem 1rem;
  overflow-y: auto;
  
  /* Force full viewport coverage */
  min-width: 100vw;
  min-height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  
  /* Ensure proper stacking */
  box-sizing: border-box;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-size: 1.25rem;
  font-weight: 700;
  padding: 1rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileQuoteButton = styled(Link)`
  background: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 800;
  padding: 1rem 1.5rem;
  border-radius: 25px;
  text-align: center;
  margin-top: 1rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const SearchOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(1, 103, 95, 0.85);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  z-index: ${props => props.theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  /* Ensure proper centering on all devices */
  min-height: 100vh;
  min-width: 100vw;
  
  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 1.5rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 1rem;
  }
`;

const SearchModal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  width: 100%;
  max-width: 600px;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-20px)'};
  transition: transform 0.4s ease;
  
  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 2rem;
    max-width: 500px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 1.5rem;
    max-width: 450px;
  }
`;

const SearchForm = styled.form`
  position: relative;
  width: 100%;
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.25rem;
  color: ${props => props.theme.colors.primary};
  z-index: 1;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    left: 1rem;
    font-size: 1.1rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    left: 0.875rem;
    font-size: 1rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1.25rem 1.5rem;
  padding-left: 3.5rem;
  padding-right: 6rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 500;
  outline: none;
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.background};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 15px rgba(1, 103, 95, 0.15);
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 1rem 1.25rem;
    padding-left: 3rem;
    padding-right: 5rem;
    font-size: 1.1rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 0.875rem 1rem;
    padding-left: 2.75rem;
    padding-right: 4.5rem;
    font-size: 1rem;
  }
`;

const SearchSubmitButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-50%) scale(1.05);
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.border};
  font-size: 1.25rem;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [siteSettings, setSiteSettings] = useState({
    contactInfo: {
      email: 'admin@rplsupport.com.au',
      phone: '+61 433 098 399',
      phoneSecondary: '+61 415 820 320',
      businessHours: 'Mon - Sat: 8:00 am - 7:00 pm'
    },
    socialMedia: {
      facebook: 'https://www.facebook.com/profile.php?id=61570517976682',
      instagram: 'https://l.instagram.com/?u=https%3A%2F%2Fwww.facebook.com%2Frplsupport.com.au%3Fmibextid%3DLQQJ4d&e=AT3GqV8_JTXeqmox0o6-gM3P4nkwJfIKyidiTA3vShUNeVS-MZY9MLHj9s1sim2frnzj3yjGit1f5-ysB0eH5QKPmn7xnpWIDmjNT3s',
      youtube: 'https://www.youtube.com/@RPLSupport_Aus'
    }
  });
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/courses', label: 'Courses' },
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact Us' },
    { path: '/how-rpl-works', label: 'How RPL Works' }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?search=${encodeURIComponent(searchQuery.trim())}`;
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

    // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/public/site-settings');
        if (response.data) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
        // Use default settings if API fails
        setSiteSettings({
          contactInfo: {
            email: 'admin@rplsupport.com.au',
            phone: '+61 433 098 399',
            phoneSecondary: '+61 415 820 320',
            businessHours: 'Mon - Sat: 8:00 am - 7:00 pm'
          },
          socialMedia: {
            facebook: 'https://www.facebook.com/profile.php?id=61570517976682',
            instagram: 'https://l.instagram.com/?u=https%3A%2F%2Fwww.facebook.com%2Frplsupport.com.au%3Fmibextid%3DLQQJ4d&e=AT3GqV8_JTXeqmox0o6-gM3P4nkwJfIKyidiTA3vShUNeVS-MZY9MLHj9s1sim2frnzj3yjGit1f5-ysB0eH5QKPmn7xnpWIDmjNT3s',
            youtube: 'https://www.youtube.com/@RPLSupport_Aus'
          }
        });
      }
    };
    
    fetchSettings();
  }, []);

  return (
    <HeaderContainer>
      <HeaderBackdrop />
      <TopBar>
        <TopBarContent>
          <TopBarRight>
            <ContactInfo>
              <FiMail />
              <span>
                <ContactLink href={`mailto:${siteSettings.contactInfo.email}`}>
                  {siteSettings.contactInfo.email}
                </ContactLink>
              </span>
            </ContactInfo>
            <ContactInfo>
              <FiPhone />
              <span>{siteSettings.contactInfo.businessHours}</span>
            </ContactInfo>
            <SocialIcons>
              {[
                { icon: FiFacebook, url: siteSettings.socialMedia.facebook, label: 'Facebook' },
                { icon: FiInstagram, url: siteSettings.socialMedia.instagram, label: 'Instagram' },
                { icon: FiYoutube, url: siteSettings.socialMedia.youtube, label: 'YouTube' }
              ].filter(social => social.url && social.url !== 'javascript:void(0)' && social.url !== '#').map((social) => (
                <SocialIcon
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  <social.icon />
                </SocialIcon>
              ))}
            </SocialIcons>
          </TopBarRight>
        </TopBarContent>
      </TopBar>

      <MainHeader>
        <MainHeaderContent>
          <Logo to="/">
            <LogoIcon src="/logo-wide.jpeg" alt="RPL Support Logo" />
          </Logo>

          <Navigation>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                {item.label}
              </NavLink>
            ))}
          </Navigation>

          <HeaderActions>
            <SearchButton onClick={() => setSearchOpen(true)}>
              <FiSearch />
            </SearchButton>
            {isAuthenticated && (
              <DashboardBtn to="/admin/dashboard" className="desktop-only">
                <FiGrid />
                Dashboard
              </DashboardBtn>
            )}
            <GetStartedBtn to="/free-quote" className="desktop-only">Free Quote</GetStartedBtn>
            <MobileMenuButton onClick={() => {
              console.log('Mobile menu button clicked');
              setMobileMenuOpen(true);
            }}>
              <FiMenu />
            </MobileMenuButton>
          </HeaderActions>
        </MainHeaderContent>
      </MainHeader>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuOpen} style={{
        transform: `translateX(${mobileMenuOpen ? '0' : '100%'})`,
        visibility: mobileMenuOpen ? 'visible' : 'hidden'
      }}>
        <MobileMenuHeader>
          <Logo to="/" onClick={closeMobileMenu}>
            <LogoIcon src="/logo-wide.jpeg" alt="RPL Support Logo" />
          </Logo>
          <MobileMenuButton onClick={closeMobileMenu}>
            <FiX />
          </MobileMenuButton>
        </MobileMenuHeader>
        <MobileNav>
          {navItems.map((item) => (
            <MobileNavLink
              key={item.path}
              to={item.path}
              onClick={closeMobileMenu}
            >
              {item.label}
            </MobileNavLink>
          ))}
          {isAuthenticated && (
            <MobileNavLink
              to="/admin/dashboard"
              onClick={closeMobileMenu}
              style={{
                background: 'var(--secondary-color)',
                color: 'white',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                justifyContent: 'center'
              }}
            >
              <FiGrid />
              Dashboard
            </MobileNavLink>
          )}
          <MobileQuoteButton to="/free-quote" onClick={closeMobileMenu}>
            Free Quote
          </MobileQuoteButton>
        </MobileNav>
      </MobileMenu>

      {/* Search Overlay */}
      <SearchOverlay isOpen={searchOpen} onClick={closeSearch}>
        <SearchModal onClick={(e) => e.stopPropagation()} isOpen={searchOpen}>
          <CloseButton onClick={closeSearch}>
            <FiX />
          </CloseButton>
          <h2 style={{ marginBottom: '1.5rem', textAlign: 'center', color: '#333' }}>Find Your Qualification</h2>
          <SearchForm onSubmit={handleSearch}>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search for your qualification here..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            <SearchSubmitButton type="submit">
              Search
            </SearchSubmitButton>
          </SearchForm>
          <p style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
            Enter a qualification name, code or keyword to find relevant courses
          </p>
        </SearchModal>
      </SearchOverlay>
    </HeaderContainer>
  );
};

export default Header;